import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { IonPage, IonHeader, IonContent } from "@ionic/react";
import { Topbar } from "./Topbar";
import { Sidebar } from "./Sidebar";
import { NotificationPermissionBanner } from "@/components/NotificationPermissionBanner";
import { InstallPromptBanner } from "@/components/InstallPromptBanner";
import { ShiftBanner } from "@/features/shifts/components/ShiftBanner";
import { useResponsive } from "@/hooks/use-responsive";
import { useNotificationNavigation } from "@/hooks/use-notification-navigation";
import { useNotificationRefresh } from "@/hooks/use-notification-refresh";
import { useAutoLogout } from "@/hooks/use-auto-logout";
import { cn } from "@/shared/utils/cn";
import Menu from "./Menu";
import Header from "./Header";
import { MessageComposer } from "@/shared/components/MessageComposer";
import { useMessageContext } from "@/contexts/MessageContext";
import { MessageContextWrapper } from "./MessageContextWrapper";
import { MessageProvider } from "@/contexts/MessageContext";
import { useParams } from "react-router-dom";

function AppLayoutFooter({ showComposer }: { showComposer: boolean }) {
  const { onSendMessage, isPending } = useMessageContext();
  const { taskId } = useParams<{ taskId: string }>();
  const taskIdNumber = taskId ? parseInt(taskId, 10) : null;
  
  if (!showComposer) {
    return null;
  }

  return (
    <MessageComposer 
      onSendMessage={onSendMessage!} 
      isPending={!!isPending}
      taskId={taskIdNumber && taskIdNumber > 0 ? taskIdNumber : null}
    />
  );
}

export function AppLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { taskId } = useParams<{ taskId: string }>();
  const { isMobile, isTablet, isDesktop } = useResponsive();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useNotificationNavigation();
  useNotificationRefresh();
  useAutoLogout();

  // ✅ Close the drawer whenever the route changes
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname, location.search]);

  const path = location?.pathname ?? "/";
  const isCannotCompletePage = path.includes("/cannot-complete");
  const isConfirmCompletePage = path.includes("/confirm-complete");
  const isReschedulePage = path.includes("/reschedule");
  const isConfirmStartShiftPage = path.includes("/shifts/confirm-start");
  const isConfirmEndShiftPage = path.includes("/shifts/confirm-end");
  const isShiftDetailsPage = path.match(/^\/shifts\/\d+$/);
  const isShiftsPage = path === "/shifts";
  const isShiftHistoryPage = path === "/shifts/history";
  const isLeaveHistoryPage = path === "/leave";
  const isPatrolSuccessPage = path === "/patrol/scan/success";
  const isTaskDetails = path.startsWith("/tasks/") && path !== "/tasks" && !isCannotCompletePage && !isConfirmCompletePage && !isReschedulePage;
  const isMessagesPage = path === "/messages";
  const isCalendarPage = path === "/tasks";
  const showComposer = isTaskDetails || isMessagesPage; // Show composer on task details and messages pages
  const shouldProvideContext = isTaskDetails || isMessagesPage; // Provide context for both task details and messages pages

  // Check if viewing a task instance (has instance ID in URL)
  const searchParams = new URLSearchParams(location.search);
  const hasInstanceId = searchParams.has("instance");

  // Determine back button href:
  // - If on confirm start shift page: go to shifts
  // - Otherwise: go to shifts (tasks routes redirect to shifts)
  const backHref = "/shifts";

  // Default empty context for pages that don't need message functionality
  const defaultContext = {
    onSendMessage: undefined,
    isPending: false
  };

  const managesOwnScroll = isTaskDetails || isMessagesPage;

  return (
    <MessageProvider {...defaultContext}>
      {/* Only show hamburger menu on mobile/tablet */}
      {!isDesktop && <Menu />}
      <IonPage id="main-content">
        <Header 
          title={
            isShiftsPage ? "Scheduled Shifts" :
            isShiftHistoryPage ? "Shift History" :
            isLeaveHistoryPage ? "Leave History" :
            isShiftDetailsPage ? "Shift Details" :
            isPatrolSuccessPage ? "Scan successful" : undefined
          }
          showBackButton={isTaskDetails || isCannotCompletePage || isConfirmCompletePage || isReschedulePage || isConfirmStartShiftPage || isConfirmEndShiftPage || isShiftDetailsPage || isShiftHistoryPage || isLeaveHistoryPage || isPatrolSuccessPage} 
          backHref={isShiftDetailsPage || isShiftHistoryPage ? "/shifts" : backHref} 
        />
        {/* 🔴 Move wrapper here so it covers BOTH content and footer */}
        <MessageContextWrapper 
          shouldProvideContext={shouldProvideContext}
          key={location.pathname} // Force re-mount when route changes
        >
          <IonContent
            fullscreen
            className=""
            style={managesOwnScroll ? ({'--offset-bottom':'0px','--padding-bottom':'0px'} as React.CSSProperties) : undefined}
            scrollY={!managesOwnScroll}
          >
            <div className={cn(isCalendarPage ? "h-screen bg-gray-50" : "h-full bg-gray-50", "max-w-full overflow-x-hidden")}>
              <div className="flex h-full min-h-0 w-full max-w-full min-w-0">
                {/* Desktop sidebar - always visible, but hidden on mobile/tablet */}
                <Sidebar isOpen={isDesktop ? true : sidebarOpen} onClose={() => setSidebarOpen(false)} />
                <main
                  className={cn(
                    // `min-w-0` stops a flex-1 `<main>` from growing past the
                    // viewport when a descendant (e.g. a wide grid cell)
                    // would otherwise force it wider on mobile webviews.
                    isCalendarPage ? "flex-1 min-w-0 h-screen" : "flex flex-col h-full min-h-0 flex-1 min-w-0",
                    // ✅ Desktop keeps a fixed gutter; mobile/tablet do NOT push content when drawer is open
                    "lg:ml-64"
                  )}
                >
                  {/* ✅ Banner inside main content area to avoid clipping */}
                  <InstallPromptBanner />
                  <NotificationPermissionBanner />
                  {!isConfirmStartShiftPage && !isConfirmEndShiftPage && !isShiftsPage && !isShiftHistoryPage && !isShiftDetailsPage && !isPatrolSuccessPage && <ShiftBanner />}
                  {children}
                </main>
              </div>
            </div>
          </IonContent>

          {/* ✅ Footer is now inside the provider and will get a real onSendMessage */}
          {showComposer && (
            <div className="lg:ml-64">
              <AppLayoutFooter showComposer={showComposer} />
            </div>
          )}
        </MessageContextWrapper>
      </IonPage>
    </MessageProvider>
  );
}
